import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useI18n } from "../i18n";
import { useCustomerAuth } from "../AuthContext";
import { useTheme } from "../ThemeContext";
import { Globe, Menu, X, User as UserIcon, LogOut, BookOpen, Sun, Moon } from "lucide-react";

export default function CustomerNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { lang, setLang, t } = useI18n();
  const { user, logout } = useCustomerAuth();
  const { theme, toggleTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(false);

  const isActive = (path) => location.pathname === path;

  const NavLink = ({ to, label }) => (
    <button
      onClick={() => { navigate(to); setOpen(false); }}
      className={`text-sm font-medium transition-colors ${isActive(to) ? "text-red-600 dark:text-red-400" : "text-gray-800 dark:text-zinc-200 hover:text-red-600 dark:hover:text-red-400"}`}
    >
      {label}
    </button>
  );

  const initials = user
    ? (user.name?.trim()?.split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase()
      || user.email?.[0]?.toUpperCase() || "U")
    : "";

  return (
    <header className="sticky top-0 z-40 bg-white/95 dark:bg-zinc-900/95 backdrop-blur border-b border-gray-100 dark:border-zinc-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
        <button onClick={() => navigate("/")} className="flex items-center select-none" aria-label="DAMS Home">
          <img
            src={theme === "dark" ? "/dams-logo-dark.png" : "/dams-logo.jpg"}
            alt="DAMS Rent A Car, S.R.L."
            className="h-10 sm:h-12 w-auto object-contain"
            draggable="false"
          />
        </button>

        <nav className="hidden md:flex items-center gap-7">
          <NavLink to="/" label={t("nav.home")} />
          <NavLink to="/cars" label={t("nav.cars")} />
          <NavLink to="/locations" label={t("nav.locations")} />
          <NavLink to="/about" label={t("nav.about")} />
          <NavLink to="/contact" label={t("nav.contact")} />
        </nav>

        <div className="hidden md:flex items-center gap-3 relative">
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle"
            aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            className="w-9 h-9 rounded-full inline-flex items-center justify-center text-gray-700 hover:text-red-600 hover:bg-gray-100 dark:text-zinc-300 dark:hover:text-red-400 dark:hover:bg-zinc-800 transition-colors"
          >
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <button
            onClick={() => setLang(lang === "en" ? "es" : "en")}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:text-red-600 dark:text-zinc-300 dark:hover:text-red-400 transition-colors"
            aria-label="Toggle language"
          >
            <Globe size={16} /> {lang === "en" ? "EN" : "ES"}
          </button>

          {user ? (
            <div className="relative">
              <button
                onClick={() => setMenu((m) => !m)}
                className="inline-flex items-center gap-2 hover:opacity-80"
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-red-500 text-white font-bold text-xs flex items-center justify-center">{initials}</div>
                )}
                <span className="text-sm font-semibold text-gray-800 max-w-[120px] truncate">{user.name || user.email}</span>
              </button>
              {menu && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-50" onMouseLeave={() => setMenu(false)}>
                  <button onClick={() => { setMenu(false); navigate("/profile"); }} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 inline-flex items-center gap-2">
                    <UserIcon size={14} /> {t("nav.signIn") === "Sign In" ? "Profile" : "Perfil"}
                  </button>
                  <button onClick={() => { setMenu(false); navigate("/my-bookings"); }} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 inline-flex items-center gap-2">
                    <BookOpen size={14} /> {t("nav.myBookings")}
                  </button>
                  <div className="border-t border-gray-100 my-1" />
                  <button onClick={async () => { setMenu(false); await logout(); navigate("/"); }} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 inline-flex items-center gap-2 text-red-600">
                    <LogOut size={14} /> Sign out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <button onClick={() => navigate("/sign-in")} className="pill-btn ghost" style={{ padding: "0.55rem 1rem" }}>
                {t("nav.signIn")}
              </button>
              <button onClick={() => navigate("/cars")} className="pill-btn primary" style={{ padding: "0.55rem 1.2rem" }}>
                {t("hero.ctaSearch")}
              </button>
            </>
          )}
        </div>

        <button className="md:hidden text-gray-800" onClick={() => setOpen((o) => !o)} aria-label="Toggle menu">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-gray-100 dark:border-zinc-800 bg-white dark:bg-zinc-900">
          <div className="px-4 py-4 flex flex-col gap-3">
            <NavLink to="/" label={t("nav.home")} />
            <NavLink to="/cars" label={t("nav.cars")} />
            <NavLink to="/locations" label={t("nav.locations")} />
            <NavLink to="/about" label={t("nav.about")} />
            <NavLink to="/contact" label={t("nav.contact")} />
            {user ? (
              <>
                <NavLink to="/profile" label="Profile" />
                <NavLink to="/my-bookings" label={t("nav.myBookings")} />
                <button onClick={async () => { await logout(); setOpen(false); navigate("/"); }} className="text-sm font-medium text-red-600 text-left">Sign out</button>
              </>
            ) : (
              <>
                <NavLink to="/sign-in" label={t("nav.signIn")} />
                <NavLink to="/sign-up" label="Sign up" />
              </>
            )}
            <div className="border-t border-gray-100 dark:border-zinc-800 pt-3 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={toggleTheme}
                  data-testid="theme-toggle-mobile"
                  aria-label="Toggle theme"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-zinc-300"
                >
                  {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
                  {theme === "dark" ? "Light" : "Dark"}
                </button>
                <button onClick={() => setLang(lang === "en" ? "es" : "en")} className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-zinc-300">
                  <Globe size={16} /> {lang === "en" ? "English" : "Español"}
                </button>
              </div>
              <button onClick={() => { navigate("/cars"); setOpen(false); }} className="pill-btn primary" style={{ padding: "0.5rem 1rem" }}>
                {t("hero.ctaSearch")}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
